import React,{ useState,useEffect, useMemo } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import {colors} from '../components/Theme/WebTheme';
import {  SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import { Box, FormControl, InputLabel, MenuItem, Select, Grid } from '@mui/material';

export default function DriverEarning() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const userdata = useSelector(state => state.usersdata);
  const settings = useSelector(state => state.settingsdata.settings);
  const driverearningdata = useSelector(state => state.driverearningdata);
  
  // Create a map of driverUId to email
  const driverEmailMap = useMemo(() => {
    const map = {};
    if (userdata.users) {
      userdata.users.forEach(user => {
        if (user.usertype === 'driver' && user.id) {
          map[user.id] = settings.AllowCriticalEditsAdmin ? user.email : t("hidden_demo");
        }
      });
    }
    return map;
  }, [userdata.users, settings.AllowCriticalEditsAdmin, t]);

  // Create lookup for driver filter based on driver names in earnings data
  const driverLookup = useMemo(() => {
    const lookup = {};
    if (driverearningdata.driverearnings) {
      driverearningdata.driverearnings.forEach(earning => {
        if (earning.driverName && !lookup[earning.driverName]) {
          lookup[earning.driverName] = earning.driverName;
        }
      });
    }
    return lookup;
  }, [driverearningdata.driverearnings]);

  // Create list of unique drivers for dropdown filter
  const driverOptions = useMemo(() => {
    const drivers = [];
    if (driverearningdata.driverearnings) {
      const uniqueDrivers = new Set();
      driverearningdata.driverearnings.forEach(earning => {
        if (earning.driverUId && earning.driverName && !uniqueDrivers.has(earning.driverUId)) {
          uniqueDrivers.add(earning.driverUId);
          const email = driverEmailMap[earning.driverUId] || '';
          drivers.push({
            id: earning.driverUId,
            name: earning.driverName,
            email: email,
          });
        }
      });
    }
    return drivers.sort((a, b) => a.name.localeCompare(b.name));
  }, [driverearningdata.driverearnings, driverEmailMap]);

  // Create list of unique years for dropdown filter
  const yearOptions = useMemo(() => {
    const years = new Set();
    if (driverearningdata.driverearnings) {
      driverearningdata.driverearnings.forEach(earning => {
        if (earning.year) {
          years.add(earning.year);
        }
      });
    }
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [driverearningdata.driverearnings]);

  // Create list of unique months for dropdown filter
  const monthOptions = useMemo(() => {
    const months = new Set();
    if (driverearningdata.driverearnings) {
      driverearningdata.driverearnings.forEach(earning => {
        if (earning.monthsName) {
          months.add(earning.monthsName);
        }
      });
    }
    return Array.from(months).sort();
  }, [driverearningdata.driverearnings]);

  const [selectedDriverFilter, setSelectedDriverFilter] = useState('all');
  const [selectedYearFilter, setSelectedYearFilter] = useState('all');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState('all');
  const [filteredData, setFilteredData] = useState([]);

  // Filter data based on selected filters
  useEffect(() => {
    if (driverearningdata.driverearnings) {
      let filtered = driverearningdata.driverearnings;

      // Filter by driver
      if (selectedDriverFilter !== 'all') {
        filtered = filtered.filter(earning => earning.driverUId === selectedDriverFilter);
      }

      // Filter by year
      if (selectedYearFilter !== 'all') {
        filtered = filtered.filter(earning => earning.year === parseInt(selectedYearFilter));
      }

      // Filter by month
      if (selectedMonthFilter !== 'all') {
        filtered = filtered.filter(earning => earning.monthsName === selectedMonthFilter);
      }

      setFilteredData(filtered);
    } else {
      setFilteredData([]);
    }
  }, [driverearningdata.driverearnings, selectedDriverFilter, selectedYearFilter, selectedMonthFilter]);

  const columns =  [
      { title: t('year'),field: 'year',
      },
      { title: t('months'), field: 'monthsName', },
      { title: t('driver_id'), field: 'driverUId', 
      },
      { 
        title: t('driver_name'), 
        field: 'driverName',
        lookup: driverLookup,
      },
      { 
        title: t('email'), 
        field: 'driverEmail',
        render: rowData => driverEmailMap[rowData.driverUId] || t("N/A"),
        exportTransformer: (rowData) => driverEmailMap[rowData.driverUId] || '',
      },
      { title: t('booking_count'), field: 'total_rides', 
    },
      { title: t('vehicle_reg_no'), field: 'driverVehicleNo', 
      },
      { title: t('earning_amount'), field: 'driverShare', 
    }
  ];

  const [data, setData] = useState([]);

  useEffect(()=>{
        setData(filteredData);
  },[filteredData]);

  const [selectedRow, setSelectedRow] = useState(null);
  
  return (
    driverearningdata.loading? <CircularLoading/>:
      <ThemeProvider theme={theme}>
        <Box sx={{ marginBottom: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl 
                fullWidth
                sx={{ 
                  backgroundColor: colors.WHITE,
                }}
              >
                <InputLabel 
                  id="driver-filter-label"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  {t('select_driver')}
                </InputLabel>
                <Select
                  labelId="driver-filter-label"
                  id="driver-filter-select"
                  value={selectedDriverFilter}
                  label={t('select_driver')}
                  onChange={(e) => setSelectedDriverFilter(e.target.value)}
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <MenuItem value="all" style={{ fontFamily: FONT_FAMILY }}>
                    {t('All')}
                  </MenuItem>
                  {driverOptions.map((driver) => (
                    <MenuItem 
                      key={driver.id} 
                      value={driver.id}
                      style={{ fontFamily: FONT_FAMILY }}
                    >
                      {driver.email ? `${driver.name} (${driver.email})` : driver.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl 
                fullWidth
                sx={{ 
                  backgroundColor: colors.WHITE,
                }}
              >
                <InputLabel 
                  id="year-filter-label"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  {t('year')}
                </InputLabel>
                <Select
                  labelId="year-filter-label"
                  id="year-filter-select"
                  value={selectedYearFilter}
                  label={t('year')}
                  onChange={(e) => setSelectedYearFilter(e.target.value)}
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <MenuItem value="all" style={{ fontFamily: FONT_FAMILY }}>
                    {t('All')}
                  </MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem 
                      key={year} 
                      value={year}
                      style={{ fontFamily: FONT_FAMILY }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl 
                fullWidth
                sx={{ 
                  backgroundColor: colors.WHITE,
                }}
              >
                <InputLabel 
                  id="month-filter-label"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  {t('months')}
                </InputLabel>
                <Select
                  labelId="month-filter-label"
                  id="month-filter-select"
                  value={selectedMonthFilter}
                  label={t('months')}
                  onChange={(e) => setSelectedMonthFilter(e.target.value)}
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <MenuItem value="all" style={{ fontFamily: FONT_FAMILY }}>
                    {t('All')}
                  </MenuItem>
                  {monthOptions.map((month) => (
                    <MenuItem 
                      key={month} 
                      value={month}
                      style={{ fontFamily: FONT_FAMILY }}
                    >
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <MaterialTable
          title={t('driver_earning_title')}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            padding: "20px",
          }}
          data={data}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={{
            pageSize: 10,
            pageSizeOptions: [10, 15, 20],
            exportButton: true,
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? colors.ROW_SELECTED :colors.WHITE
            }),
            editable:{
              backgroundColor: colors.Header_Text,
              fontSize: "0.8em",
              fontWeight: 'bold ',
            },
            headerStyle: {
              position: "sticky",
              top: "0px",
              fontSize: "0.8em",
              fontWeight: 'bold ',
              color: colors.BLACK,
              backgroundColor: SECONDORY_COLOR,
              textAlign: "center",
              border: `1px solid ${colors.TABLE_BORDER}`,
            },
            cellStyle: {
              border: `1px solid ${colors.TABLE_BORDER}`,
              textAlign: "center",
            },
            actionsColumnIndex: -1,
          }}
          localization={{
            toolbar: {
              searchPlaceholder: (t('search')),
              exportTitle: (t('export')),
            },
            pagination: {
              labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
              firstTooltip: (t('first_page_tooltip')),
              previousTooltip: (t('previous_page_tooltip')),
              nextTooltip: (t('next_page_tooltip')),
              lastTooltip: (t('last_page_tooltip'))
            },
          }}
        />
      </ThemeProvider>
  );
}
